// @workflow assistant.clarification
/**
 * Clarification state on a conversation: which question the panel shows, answering it, expiry, and
 * quoting an expired question in the next message. Every change goes through the reducer
 * (applyEvent) with the same `client.resolved` / `message.end` events the server sends, so the
 * live thread and a history replay can't drift apart.
 * Every function is immutable. No Vue or path-alias imports (node --test loads it directly).
 */
import { EVENT, PART, ROLE, MESSAGE_STATUS, REQUEST_KIND, REQUEST_STATUS, applyEvent } from './events.js';
import { statusForAnswer, quotePrefix, questionsOf } from './clarification.js';
import { cleanAnswer } from './clarificationAnswers.js';

const isAwaiting = (c, messageId) => c.messages.some((m) => m.id === messageId && m.status === MESSAGE_STATUS.AWAITING);
const isClarification = (p) => p.type === PART.CLARIFICATION;
const TEXT_SEGMENT = 'text';
const COMMAND_SEGMENT = 'command';

/**
 * The question the panel shows: a pending clarification of a reply that is awaiting. On the live
 * stream `client.request` arrives just before `message.pause`; answering before the pause would
 * race the stream, so the panel waits for it.
 * @returns {object|null} the clarification part
 */
export function pendingClarification(c) {
  for (let i = c.messages.length - 1; i >= 0; i -= 1) {
    const m = c.messages[i];
    const part = m.status === MESSAGE_STATUS.AWAITING ? m.parts.find((p) => isClarification(p) && p.status === REQUEST_STATUS.PENDING) : null;
    if (part) return part;
  }
  return null;
}

/** Clarifications that broke the contract and wait for the UI's `{ answer: { dismissed: true } }`. */
export const invalidClarifications = (c) =>
  Object.values(c.requests || {}).filter((r) => r.kind === REQUEST_KIND.CLARIFICATION && r.invalid && r.status === REQUEST_STATUS.PENDING && isAwaiting(c, r.messageId));

/** The clarification part of `requestId`, or null. */
export function clarificationPart(c, requestId) {
  for (const m of c.messages) {
    const part = m.parts.find((p) => isClarification(p) && p.requestId === requestId);
    if (part) return part;
  }
  return null;
}

/**
 * The user's answer to a pending clarification, checked against its questions
 * (clarificationAnswers.cleanAnswer): `{ answers }` or `{ dismissed: true }`, the body the UI posts.
 * The one-question composer's `{ optionIds, text }` reads as the answer to that one question.
 * @returns {object|null} null: no such pending clarification, or not a whole answer
 */
export function clarificationAnswerFor(c, requestId, answer) {
  const request = c.requests[requestId];
  if (!request || request.kind !== REQUEST_KIND.CLARIFICATION || request.status !== REQUEST_STATUS.PENDING) return null;
  return cleanAnswer(answer, questionsOf(clarificationPart(c, requestId)));
}

/**
 * Records the user's answer at once, so the panel closes and the line shows before the stream
 * resumes. Only a pending clarification takes an answer, and only a whole one (every question):
 * a second answer, or an incomplete one, changes nothing.
 * @param {object} c
 * @param {string} requestId
 * @param {{ answers: Array } | { optionIds: string[], text?: string } | { dismissed: true }} answer
 */
export function withClarificationAnswer(c, requestId, answer) {
  const clean = clarificationAnswerFor(c, requestId, answer);
  if (!clean) return c;
  return applyEvent(c, { type: EVENT.CLIENT_RESOLVED, requestId, outcome: statusForAnswer(clean), answer: clean });
}


/**
 * `expiresAt` passed while the reply waited. The stream is closed during a pause, so the server
 * can't tell the UI in time: the UI closes the request itself and ends the paused reply.
 */
export function withExpiredRequest(c, requestId) {
  const request = c.requests[requestId];
  if (!request || request.status !== REQUEST_STATUS.PENDING) return c;
  const expired = applyEvent(c, { type: EVENT.CLIENT_RESOLVED, requestId, outcome: REQUEST_STATUS.EXPIRED });
  return isAwaiting(expired, request.messageId) ? applyEvent(expired, { type: EVENT.MESSAGE_END, messageId: request.messageId }) : expired;
}

/** The expired question the next typed message quotes: in the replies since the last user turn, not quoted yet. */
function expiredToQuote(c) {
  for (let i = c.messages.length - 1; i >= 0; i -= 1) {
    const m = c.messages[i];
    if (m.role === ROLE.USER) return null;
    const part = m.parts.find((p) => isClarification(p) && p.status === REQUEST_STATUS.EXPIRED && !p.quoted);
    if (part) return { messageId: m.id, part };
  }
  return null;
}

/** The request id of that question (the thread says the next message will quote it), else null. */
export function expiredQuestionId(c) {
  const found = expiredToQuote(c);
  return found ? found.part.requestId : null;
}

/**
 * A message typed after a question expired quotes it, so AVA can pick the answer up. The question
 * is marked quoted, so a retry of that message doesn't quote it twice. A command starts a new
 * request, so it is sent as typed.
 * @param {object} c
 * @param {{ text: string, segments?: Array<{ type: string, text?: string }> }} message
 * @returns {{ conversation: object, text: string, segments?: Array }}
 */
export function withQuotedQuestion(c, { text, segments }) {
  const found = expiredToQuote(c);
  const typed = segments && segments.length ? segments : [{ type: TEXT_SEGMENT, text }];
  if (!found || typed[0].type === COMMAND_SEGMENT) return { conversation: c, text, segments };
  const prefix = quotePrefix(questionsOf(found.part).map((q) => q.question));
  const quoted = (m) => (m.id === found.messageId ? { ...m, parts: m.parts.map((p) => (p === found.part ? { ...p, quoted: true } : p)) } : m);
  return {
    conversation: { ...c, messages: c.messages.map(quoted) },
    text: `${prefix}${text}`,
    segments: [{ type: TEXT_SEGMENT, text: prefix }, ...typed],
  };
}
